import { getStore } from '../../../store';

import { createGUID } from './utils';
import { DEVELOPMENT, PRODUCTION } from 'app/config';

let identifyInterval = null;
let pageStack = [];
let actionStack = [];
/**
 * shared tracking vars
 */

export const paginationTrackingParams = {
  event: 'pagination_click',
  description: 'user clicks pagination',
};

/**
 * tracking helpers
 */
export const isTrackingDisabled = () =>
  getStore().getState().dealerLogin?.trackingDisabled;

const isAnalyticsAvailable = () => typeof window.analytics !== 'undefined';

export const isHotjarAvailable = () => typeof window.hj !== 'undefined';

export const normalizeValue = value => {
  if (typeof value === 'number') {
    return value;
  }
  if (!value) {
    return null;
  }
  return value;
};

/**
 * tracking functions
 */
export const setTrackingVersion = () => {
  if (isTrackingDisabled()) {
    return;
  }
  if (isAnalyticsAvailable()) {
    window.analytics.ready(() => {
      window.ga('set', 'appName', 'heycar');
      window.ga('set', 'appVersion', '1.0.0');
    });
  }
};

export const trackPage = page => {
  if (isTrackingDisabled()) {
    return;
  }
  const date = new Date();
  const guid = createGUID();
  const userAccount =
    // TODO: find out what kind of user we want to tracking in the dealer portal
    // should we use user id from dealer login?
    getStore().getState().authMarketplace?.user;
  const userAccountId = userAccount?.sub || null;

  const { href: url, pathname: path, search } = window.location;
  const { title, referrer } = document;

  const pageProperties = page || {
    url,
    path,
    search,
    title,
    referrer,
    hitTimestamp: date.getTime(),
    eventId: guid,
    sessionId: guid,
    userAccountId,
  };

  if (isAnalyticsAvailable() && pageStack.length === 0) {
    window.analytics.page('', pageProperties);
  } else if (PRODUCTION) {
    pageStack.push(pageProperties);
  }
};

/**
 * @description Tracking user/website interactions and push all information to tracking server
 * @export
 * @param {string} actionName Tracking Action Name
 * @param {object} [option={}] Optional Data - To logging data on client
 * @param {object} [additionalProperties={}] Additional Properties
 * @param {boolean} logTracker If true, log what was tracked
 * @returns {void}
 */
export const trackAction = (
  actionName,
  option = {},
  additionalProperties = {},
  logTracker = false,
) => {
  if (isTrackingDisabled()) {
    return;
  }
  const date = new Date();
  const guid = createGUID();
  const { category, label, value, description } = option;
  const givenValue = normalizeValue(value);
  const userAccount =
    getStore().getState().authMarketplace &&
    getStore().getState().authMarketplace.user;
  const userAccountId = (userAccount && userAccount.sub) || null;

  const eventProperties = Object.assign(additionalProperties, {
    category,
    description,
    label: label || null,
    value: givenValue,
    hitTimestamp: date.getTime(),
    userAccountId,
    // allow the eventId to be overridden by additionalproperties - this is an exception
    eventId: additionalProperties.eventId ? additionalProperties.eventId : guid,
    sessionId: additionalProperties.sessionId
      ? additionalProperties.sessionId
      : guid,
    interactedWith: additionalProperties.interactedWith
      ? additionalProperties.interactedWith
      : null,
    email: window.HEYCAR_USERID,
  });

  const trackingData = {
    actionName,
    option,
    additionalProperties: {
      ...additionalProperties,
      hitTimestamp: date.getTime(),
      eventId: guid,
      sessionId: guid,
      userAccountId,
      email: window.HEYCAR_USERID,
    },
  };

  if (isAnalyticsAvailable() && actionStack.length === 0) {
    window.analytics.track(actionName, eventProperties, {
      context: {
        ip: window.anonIp,
        page: {
          url: window.location.href,
          path: window.location.pathname,
          search: window.location.search,
          title: document.title,
          referrer: document.referrer,
        },
      },
    });
  } else if (identifyInterval && PRODUCTION) {
    // If it's on prod, push tracking data
    actionStack.push(trackingData);
  } /* istanbul ignore next */ else if (DEVELOPMENT && logTracker) {
    console.log(trackingData);
  }
};

export const trackTimeToInteraction = () => {
  if (isTrackingDisabled()) {
    return;
  }
  if (isAnalyticsAvailable()) {
    /*
     there are two conditions here, that i could not test to full coverage, because jest in our setup uses jsdom as
     the default environment which has an unoverrideable 'window.performance' object. this means the first line of each
     of these two conditions cannot be covered. Changing the environment to the 'node' environment does throw so many
     errors that i don't know where to start.
      */
    // eslint-disable-next-line no-mixed-operators
    /* istanbul ignore next */
    const domInteractive =
      (window.performance &&
        window.performance.timing &&
        window.performance.timing.domInteractive) ||
      0;
    // eslint-disable-next-line no-mixed-operators
    /* istanbul ignore next */
    const requestStart =
      (window.performance &&
        window.performance.timing &&
        window.performance.timing.requestStart) ||
      0;

    const tti = domInteractive - requestStart;
    const option = {
      category: 'performance',
      description: 'performance properties this user',
      label: 'time_to_interaction',
      value: tti || null,
    };

    trackAction('performance_tti', option);
  }
};

export const trackConnectionQuality = () => {
  if (isTrackingDisabled()) {
    return;
  }
  let connection = window && window.navigator && window.navigator.connection;

  if (!connection) {
    connection = {};
  }
  const option = {
    category: 'connection',
    description: 'connection properties of the user',
    label: `downlink:${connection.downlink || null};effectiveType:${
      connection.effectiveType || null
    };rtt:${connection.rtt || null}`,
    value: connection.downlink || 0,
  };

  trackAction('connection_performance', option);
};

export const trackIdentify = (userId, createdAt) => {
  if (isTrackingDisabled()) {
    return;
  }
  if (isAnalyticsAvailable()) {
    const userAccount = getStore().getState().authMarketplace?.user;

    // TODO: find out what kind of user we want to identify in the dealer portal
    //       the authMarketplace user will be undefined in the dealer portal
    const userAccountId = (userAccount && userAccount.sub) || null;
    window.analytics.identify(userId, {
      trackingId: userId,
      userAccountId,
      createdAt,
      email: userId,
      language: 'DE_DE_X_INFORMAL',
    });
    if (identifyInterval) {
      const pageStackCopy = pageStack.slice();

      pageStack = [];
      pageStackCopy.map(trackPage);
      trackPage();
      const actionStackCopy = actionStack.slice();

      actionStack = [];
      actionStackCopy.map(action =>
        trackAction(
          action.actionName,
          action.option,
          action.additionalProperties,
        ),
      );
      clearInterval(identifyInterval);
      identifyInterval = null;
    }
  } else if (!identifyInterval && PRODUCTION) {
    identifyInterval = setInterval(trackIdentify, 100, userId, createdAt);
  }
};

export const triggerHotjarAction = (triggerName, logTrigger = false) => {
  if (isTrackingDisabled()) {
    return;
  }

  if (isHotjarAvailable() && triggerName) {
    window.hj('trigger', triggerName);
    /* istanbul ignore next */ if (DEVELOPMENT && logTrigger) {
      console.log(`triggerHotjarAction: ${triggerName}`);
    }
  }
};
